import { useEffect, useState } from 'react';
import { useGlobalSettings } from '../../../stores';
import { getMissingObjects } from './get-missing-objects';
import {
  DiagnoseCheckFunction,
  DiagnoseCheckResult,
  DiagnoseFixResult
} from './types';

const checkFunctions: DiagnoseCheckFunction[] = [
  getMissingObjects
];

export type UseDiagnoseDesignResult = {
  /** List of checks that found issues. */
  checkResults: (DiagnoseCheckResult & { shouldFix: boolean; })[];
  close: () => void;
  fix: () => void;
  /** Results returned by applying the fixes. */
  fixResults: DiagnoseFixResult[] | null;
  /** Set whether the fix at @see indexToUpdate of @see checkResults should be applied when @see fix is called. */
  setShouldFix: (indexToUpdate: number, shouldFix: boolean) => void;
  isOpen: boolean;
};

export function useDiagnoseDesign(): UseDiagnoseDesignResult {
  const { serialized } = useGlobalSettings();
  const [checkResults, setCheckResults] = useState<UseDiagnoseDesignResult['checkResults']>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [fixResults, setResult] = useState<DiagnoseFixResult[] | null>(null);
  const { version = null } = serialized ?? {};

  useEffect(() => {
    if (!serialized) {
      return;
    }

    const newCheckResults = checkFunctions.map((check) => {
      return {
        ...check(serialized),
        shouldFix: true
      };
    }).filter(({ ok }) => {
      return !ok;
    });
    setCheckResults(newCheckResults);
    setIsOpen(!!newCheckResults.length);
  }, [version]);

  const fix = () => {
    setResult(
      checkResults.filter(({ shouldFix }) => {
        return shouldFix;
      }).map(({ fixer }) => {
        return fixer();
      })
    );
    window.apm.setSerializedChanged();
    window.apm.refreshApplicationTree();
    window.apm.reactDOMComponents.renderApplicationPages();
  };

  return {
    close: () => setIsOpen(false),
    checkResults,
    fix,
    fixResults,
    setShouldFix: (indexToUpdate, shouldFix) => {
      setCheckResults(checkResults.map((checkResult, index) => {
        return (index !== indexToUpdate) ? checkResult : (
          {
            ...checkResult,
            shouldFix
          }
        );
      }));
    },
    isOpen
  };
}
